import type { Object3D, Group } from 'three';
import { Material, Mesh } from 'three';

export const traverseObject3DMaterials = (
  object3D: Object3D,
  callback: (material: Material) => void,
) => {
  object3D.traverse(child => {
    if (child instanceof Mesh && child.material instanceof Material) {
      callback(child.material);
    }
  });
};

export function isMesh(object3D: Object3D): object3D is Mesh {
  return ((object3D as unknown) as Mesh).isMesh === true;
}

export function isGroup(object3D: Object3D): object3D is Group {
  return ((object3D as unknown) as Group).isGroup === true;
}

function cloneMesh(mesh: Mesh): Mesh {
  // @ts-ignore
  return new mesh.constructor(mesh.geometry, mesh.material);
}

type Dispose = () => void;

export function makeOverflowMeshes(mesh: Mesh, offset: number = 1, parent?: Object3D): Dispose {
  const root = parent ?? mesh.parent;
  if (root === null || root === undefined)
    throw new Error('No valid parent to add overflow meshes to');

  const leftMesh = cloneMesh(mesh);
  const rightMesh = cloneMesh(mesh);
  leftMesh.position.x = mesh.position.x - offset;
  rightMesh.position.x = mesh.position.x + offset;
  leftMesh.layers.set(1);
  rightMesh.layers.set(1);

  mesh.layers.set(1);
  root.add(leftMesh);
  root.add(rightMesh);

  return () => {
    root.remove(leftMesh);
    root.remove(rightMesh);
  };
}

// technically not accurate to how three sorts objects, but good enough for editor scene structure
export function inheritedRenderOrder(object3d: Object3D) {
  let it = object3d;
  while (it.renderOrder === 0 && it.parent !== null && isGroup(it.parent)) {
    it = it.parent;
  }
  return it.renderOrder;
}

export function groupOrder(object3d: Object3D) {
  let it = object3d;
  while (it.parent !== null && !isGroup(it)) it = it.parent;
  if (isGroup(it)) return it.renderOrder;
  else return 0;
}

type EventName = keyof WindowEventMap;

export function bubbleInputEvent(eventName: EventName, object3d: Object3D) {
  const handler = object3d.userData?.[eventName];
  if (handler !== undefined) handler();
  if (object3d.parent !== null) bubbleInputEvent(eventName, object3d.parent);
}

export function bubbleHasInputEvent(eventNames: EventName[], object3d: Object3D): boolean {
  if (eventNames.some(it => object3d.userData?.[it] !== undefined)) return true;
  else if (object3d.parent !== null) return bubbleHasInputEvent(eventNames, object3d.parent);
  else return false;
}
